import { DraggableAttributes, DraggableSyntheticListeners } from '@dnd-kit/core';
import { MoreVert as MoreIcon, AddCircle as PlusCircle } from '@mui/icons-material';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import { alpha } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';

import { ChapterUnansweredCommentsBadge } from './ChapterUnansweredCommentsBadge';

import { NarrativeSummary } from '@/lib/types';

interface ChapterColumnHeaderProps {
  chapter: NarrativeSummary;
  scenesCount: number;
  isEditing: boolean;
  nameValue: string;
  setNameValue: (val: string) => void;
  onSaveName: () => void;
  setEditingId: (id: string | null) => void;
  onMenuOpen: (event: React.MouseEvent<HTMLElement>) => void;
  onCreateNextChapter: () => void;
  onOpenEditor?: () => void;
  isOpeningEditor?: boolean;
  isChangingStatus?: boolean;
  dragAttributes?: DraggableAttributes;
  dragListeners?: DraggableSyntheticListeners;
}

export function ChapterColumnHeader({
  chapter,
  scenesCount,
  isEditing,
  nameValue,
  setNameValue,
  onSaveName,
  onMenuOpen,
  onCreateNextChapter,
  onOpenEditor,
  isOpeningEditor = false,
  isChangingStatus = false,
  dragAttributes,
  dragListeners
}: ChapterColumnHeaderProps) {
  const { t } = useTranslation();

  const getStatusBadge = (status?: string) => {
    if (status === 'preview') {
      return (
        <Tooltip title={t('chapters.publish.status_preview_help')} arrow><Chip
          label={t('chapters.publish.status_preview')}
          size="small"
          sx={{
            height: 18,
            fontSize: '0.55rem',
            fontWeight: 700,
            bgcolor: (theme) => alpha(theme.palette.info.main, 0.15),
            color: 'info.main',
            borderRadius: 1,
            textTransform: 'uppercase',
            letterSpacing: '0.05em'
          }}
        /></Tooltip>
      );
    } else if (status === 'public') {
      return (
        <Tooltip title={t('chapters.publish.status_public_help')} arrow><Chip
          label={t('chapters.publish.status_public')}
          size="small"
          sx={{
            height: 18,
            fontSize: '0.55rem',
            fontWeight: 700,
            bgcolor: (theme) => alpha(theme.palette.success.main, 0.15),
            color: 'success.main',
            borderRadius: 1,
            textTransform: 'uppercase',
            letterSpacing: '0.05em'
          }}
        /></Tooltip>
      );
    }
    return (
      <Tooltip title={t('chapters.publish.status_draft_help')} arrow><Chip
        label={t('chapters.publish.status_draft')}
        size="small"
        sx={{
          height: 18,
          fontSize: '0.55rem',
          fontWeight: 700,
          bgcolor: (theme) => alpha(theme.palette.text.secondary, 0.1),
          color: 'text.secondary',
          borderRadius: 1,
          textTransform: 'uppercase',
          letterSpacing: '0.05em'
        }}
      /></Tooltip>
    );
  };

  return (
    <Stack
      spacing={1}
      sx={{ mt: 0.5, mb: 1, px: 0.5 }}
      {...(!isEditing ? dragAttributes : {})}
      {...(!isEditing ? dragListeners : {})}
    >
      <Box sx={{ minHeight: 24, display: 'flex', alignItems: 'center' }}>
        {isEditing ? (
          <TextField
            value={nameValue}
            onChange={(e) => setNameValue(e.target.value)}
            onBlur={onSaveName}
            onKeyDown={(e) => e.key === 'Enter' && onSaveName()}
            autoFocus
            size="small"
            variant="standard"
            fullWidth
            sx={{ '& .MuiInputBase-input': { fontWeight: 600, p: 0, fontSize: '0.875rem' } }}
          />
        ) : (
          <Typography
            variant="h6"
            noWrap
            sx={{
              color: 'text.primary',
              flexGrow: 1,
              cursor: onOpenEditor ? 'pointer' : 'default',
              borderRadius: 1,
              px: 0.5,
              py: 0.25,
              mx: -0.5,
              my: -0.25,
              transition: 'background-color 0.2s ease',
              ...(onOpenEditor && {
                '&:hover': {
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1)
                }
              })
            }}
            onClick={(event) => { if (onOpenEditor) { event.stopPropagation(); onOpenEditor(); } }}
          >
            {chapter.name}
            {isOpeningEditor && <CircularProgress size={14} sx={{ ml: 1, verticalAlign: 'middle' }} />}
          </Typography>
        )}
      </Box>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ height: 28 }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          {chapter.id !== 'unassigned' && (isChangingStatus
            ? <CircularProgress size={16} color={'inherit'} />
            : getStatusBadge(chapter.publishStatus))}
          {chapter.id !== 'unassigned' && (
            <ChapterUnansweredCommentsBadge mainContentId={chapter.mainContentId} />
          )}

          <Chip
            label={scenesCount}
            size="small"
            sx={{
              height: 18,
              minWidth: 18,
              fontSize: '0.6rem',
              fontWeight: 700,
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.15),
              color: 'primary.main',
              borderRadius: 1
            }}
          />
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center">
          {chapter.id !== 'unassigned' && (
            <>
              <PlusCircle
                sx={{
                  fontSize: 20,
                  color: 'text.secondary',
                  opacity: 0.4,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    opacity: 1,
                    color: 'primary.main',
                    transform: 'scale(1.1)'
                  }
                }}
                onClick={onCreateNextChapter}
              />
              <IconButton
                size="small"
                onClick={onMenuOpen}
                disabled={isChangingStatus}
                sx={{ p: 0.5, color: 'text.secondary', opacity: 0.4, '&:hover': { opacity: 1, color: 'primary.main' } }}
              >
                <MoreIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
}
